import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from 'api';
import useCallbackAsync from 'hooks/useCallbackAsync';
import {
  ActiveSuggestedAction,
  SUGGESTED_ACTION_MUTE_DAYS,
} from 'router/subrouters/DataRoom/models/Dashboard';
import { refetchEnterpriseSuggestedActions } from '../utils';
import { SUGGESTION_PAGE } from 'models/SuggestedActions';
import { EnterpriseSuggestedActionUndoHandlers } from './useEnterpriseUndoActions';

const useEnterpriseThreeDotsOptions = (
  dataRoomId: string,
  suggestion: ActiveSuggestedAction,
  {
    setDismissedAction,
    setDismissUndoTimeout,
    setDontRemindUndoTimeout,
    setMutedActionType,
    setMuteForAllDealsUndoTimeout,
    resetPage,
  }: EnterpriseSuggestedActionUndoHandlers,
) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { type } = suggestion;

  const [onDismiss, isDissmising] = useCallbackAsync(async () => {
    await api.enterpriseSuggestedActions.muteSuggestedAction(
      dataRoomId,
      type,
      SUGGESTION_PAGE.ENTERPRISE_DASHBOARD,
      SUGGESTED_ACTION_MUTE_DAYS,
    );

    await refetchEnterpriseSuggestedActions(queryClient);
    setDismissedAction({ type, dataRoomId });
    setDismissUndoTimeout();
  });

  const [onDontRemindMe, isDontReminding] = useCallbackAsync(async () => {
    await api.enterpriseSuggestedActions.muteSuggestedAction(
      dataRoomId,
      type,
      SUGGESTION_PAGE.ENTERPRISE_DASHBOARD,
    );

    await refetchEnterpriseSuggestedActions(queryClient);
    setDismissedAction({ type, dataRoomId });
    setDontRemindUndoTimeout();
  });

  const [onMuteForAllDeals, isMutingForAllDeals] = useCallbackAsync(
    async () => {
      await api.enterpriseSuggestedActions.muteSuggestedActionForAllDeals(type);

      await refetchEnterpriseSuggestedActions(queryClient);
      setMutedActionType(type);
      setMuteForAllDealsUndoTimeout();
      resetPage();
    },
  );

  const threeDotsOptions = useMemo(
    () => [
      {
        label: t('dismiss'),
        disabled: isDissmising,
        onClick: onDismiss,
      },
      {
        label: t('dontRemindMeAgain'),
        disabled: isDontReminding,
        onClick: onDontRemindMe,
      },
      {
        label: t('muteForAllDeals'),
        disabled: isMutingForAllDeals,
        onClick: onMuteForAllDeals,
      },
    ],
    [
      isDissmising,
      isDontReminding,
      isMutingForAllDeals,
      onDismiss,
      onDontRemindMe,
      onMuteForAllDeals,
      t,
    ],
  );

  return {
    isDissmising,
    isDontReminding,
    isMutingForAllDeals,
    threeDotsOptions,
  };
};

export default useEnterpriseThreeDotsOptions;
